import React, { createContext, useContext, useState } from "react";
import {
  FUEL_TYPE,
  normaliseFuelType,
  normaliseMeterType,
  REQUIRED_TARIFF_FIELDS,
  SUPPLIER_FUEL_TYPE,
} from "../constants/tariffInfo";
import usePersistedState from "../hooks/usePersistedState";
import Client from "../utils/client";

const TariffContext = createContext();

const TariffProvider = ({ children }) => {
  const c = new Client();

  const [isFetchingCurrentTariff, setIsFetchingCurrentTariff] = useState(true);
  const [isFetchingMonthlySpend, setIsFetchingMonthlySpend] = useState(true);
  const [isTariffDataLoading, setIsTariffDataLoading] = useState(true);
  const [isSupplierDataLoading, setIsSupplierDataLoading] = useState(true);
  const [supplierOptions, setSupplierOptions] = useState({}); // shape: { fuelType: [{value, label, id}] }
  const [tariffOptions, setTariffOptions] = useState({
    [SUPPLIER_FUEL_TYPE.ELECGAS]: {},
    [SUPPLIER_FUEL_TYPE.ELEC]: {},
    [SUPPLIER_FUEL_TYPE.GAS]: {},
  }); // shape: { fuelType: {supplierId: [{tariff}]}}
  const [selectedSuppliers, setSelectedSuppliers] = useState({}); // shape: { fuelType: supplierId }
  const [selectedMeterTypes, setSelectedMeterTypes] = useState({}); // shape: { fuelType: meterType }
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState({}); // shape: { fuelType: paymentType }
  const [selectedFuelType, setSelectedFuelType] = useState(null); // One of SUPPLIER_FUEL_TYPE

  const [meterDetails, setMeterDetails] = usePersistedState(
    "meterDetails",
    null
  );

  const [currentTariff, setCurrentTariff] = usePersistedState(
    "currentTariff",
    null
  );

  const [quoteId, setQuoteId] = usePersistedState("quoteId", null);

  const [existingTariffElec, setExistingTariffElec] = usePersistedState(
    "existingTariffElec",
    null
  );

  const [existingTariffGas, setExistingTariffGas] = usePersistedState(
    "existingTariffGas",
    null
  );

  const [estimatedAnnualSpendElec, setEstimatedAnnualSpendElec] =
    usePersistedState("estimatedAnnualSpendElec", null);

  const [estimatedAnnualSpendGas, setEstimatedAnnualSpendGas] =
    usePersistedState("estimatedAnnualSpendGas", null);

  const [userDefinedTariffInfo, setUserDefinedTariffInfo] = usePersistedState(
    "userDefinedTariffInfo",
    {}
  );

  const fetchSupplierOptions = async (fuelType, meterType, paymentType) => {
    try {
      const results = await c.getSuppliersList(
        currentTariff?.dnoArea,
        fuelType,
        normaliseMeterType(meterType),
        paymentType
      );
      const suppliersWithTariffs = results.filter(
        (supplier) => supplier?.HasTariff === 1
      );
      const updatedSupplierOptions = supplierOptions;
      updatedSupplierOptions[normaliseFuelType(fuelType)] =
        suppliersWithTariffs.map((supplier) => ({
          value: supplier?.ProviderSupplierName,
          label: supplier?.ProviderSupplierName,
          id: supplier?.SupplierId,
        }));
      setSupplierOptions(updatedSupplierOptions);
    } catch (error) {
      console.error(error);
    }
  };

  const combinedTariffInfo = {
    ...currentTariff,
    ...userDefinedTariffInfo,
  };

  const updateTariffOptions = async () => {
    if (!selectedSuppliers) return;
    if (!Object.keys(selectedSuppliers).length) return;

    setIsTariffDataLoading(true);
    for (const fuelType of Object.keys(selectedSuppliers)) {
      const supplierName = selectedSuppliers[fuelType];
      if (supplierOptions?.[normaliseFuelType(fuelType)]) {
        const supplierId = supplierOptions[normaliseFuelType(fuelType)].find(
          (supplier) => supplier.label === supplierName
        )?.id;
        if (!tariffOptions[SUPPLIER_FUEL_TYPE.ELECGAS]?.[supplierId]) {
          // only fetch if we don't already have the data
          const response = await getTariffs(supplierId, currentTariff?.dnoArea);
          let newTariffOptions = tariffOptions;
          newTariffOptions[SUPPLIER_FUEL_TYPE.ELECGAS][supplierId] =
            response?.fuels?.[
              SUPPLIER_FUEL_TYPE.ELECGAS
            ]?.suppliers[0]?.supplierTariffs;
          newTariffOptions[SUPPLIER_FUEL_TYPE.ELEC][supplierId] =
            response?.fuels?.[
              SUPPLIER_FUEL_TYPE.ELEC
            ]?.suppliers[0]?.supplierTariffs;
          newTariffOptions[SUPPLIER_FUEL_TYPE.GAS][supplierId] =
            response?.fuels?.[
              SUPPLIER_FUEL_TYPE.GAS
            ]?.suppliers[0]?.supplierTariffs;
          setTariffOptions(newTariffOptions);
        }
      }
    }
    setIsTariffDataLoading(false);
  };

  const resetTariffInfo = () => {
    // resets all the persisted states
    setMeterDetails(null);
    setCurrentTariff(null);
    setQuoteId(null);
    setExistingTariffElec(null);
    setExistingTariffGas(null);
    setEstimatedAnnualSpendElec(null);
    setEstimatedAnnualSpendGas(null);
    setUserDefinedTariffInfo({});
    setIsFetchingCurrentTariff(true);
    setIsFetchingMonthlySpend(true);
  };

  const updateUserDefinedTariffInfo = (field, value) => {
    setUserDefinedTariffInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const deleteUserDefinedTariffInfoKey = (key) => {
    setUserDefinedTariffInfo((prev) => {
      const newData = { ...prev };
      delete newData[key];
      return newData;
    });
  };

  const getCurrentTariff = async (mpan, mprn = null) => {
    const quotesRequest = await c.getQuotesRequest(mpan, mprn);
    setCurrentTariff(quotesRequest);
    return quotesRequest;
  };

  const getTariffs = async (supplierId, fuelType) => {
    const response = await c.getSupplierTariffs(
      supplierId,
      currentTariff?.dnoArea
    );
    return response;
  };

  const getQuotes = async (mpan, mprn = null) => {
    if (!currentTariff) {
      await getCurrentTariff(mpan, mprn);
    }

    const quotesResults = await c.getQuotes(combinedTariffInfo);
    setExistingTariffElec(quotesResults?.quoteData?.existingTariffElec);
    setExistingTariffGas(quotesResults?.quoteData?.existingTariffGas);
    setQuoteId(quotesResults?.quoteId);
    setEstimatedAnnualSpendElec(
      quotesResults?.quoteData?.energySearchProfile?.resultsExistingSpendElec
    );
    setEstimatedAnnualSpendGas(
      quotesResults?.quoteData?.energySearchProfile?.resultsExistingSpendGas
    );

    return quotesResults?.quoteData?.searchResults;
  };

  const getEstimatedMonthlySpend = async () => {
    if (!combinedTariffInfo) {
      return;
    }

    // if the user has not entered their own usage or tariff info, use the current tariff info
    if (!userDefinedTariffInfo) {
      if (currentTariff?.elecSpendGbpPerMonth) {
        setEstimatedAnnualSpendElec(currentTariff?.elecSpendGbpPerMonth * 12);
      }
      if (currentTariff?.gasSpendGbpPerMonth) {
        setEstimatedAnnualSpendGas(currentTariff?.gasSpendGbpPerMonth * 12);
      }
    }

    // if user has entered their own usage or tariff, send that to getQuotes and then use the response to set the estimated spend
    try {
      const quotesResults = await c.getQuotes(combinedTariffInfo);
      const existingAnnualSpendElecGbp =
        quotesResults?.quoteData?.energySearchProfile?.resultsExistingSpendElec;
      setEstimatedAnnualSpendElec(existingAnnualSpendElecGbp);
      const existingAnnualSpendGasGbp =
        quotesResults?.quoteData?.energySearchProfile?.resultsExistingSpendGas;
      setEstimatedAnnualSpendGas(existingAnnualSpendGasGbp);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDetails = async (addressId, data) => {
    const results = await c.getMeterDetails(addressId, data);
    setMeterDetails(results);
    return results;
  };

  const fetchSwitchStatus = async () => {
    try {
      const results = await c.switchStatus();
      return { statusCode: 200, switchStatus: results };
    } catch (error) {
      console.error(error);
      return { statusCode: error?.statusCode || 500, switchStatus: null };
    }
  };

  const estimatedMonthlyKwhsElec = combinedTariffInfo?.existingkWhsElec
    ? (parseFloat(combinedTariffInfo?.existingkWhsElec) / 12).toFixed(2)
    : null;

  const estimatedMonthlyKwhsGas = combinedTariffInfo?.existingkWhsGas
    ? (parseFloat(combinedTariffInfo?.existingkWhsGas) / 12).toFixed(2)
    : null;

  const estimatedMonthlySpendElec = (
    parseFloat(estimatedAnnualSpendElec) / 12
  ).toFixed(2);

  const estimatedMonthlySpendGas = (
    parseFloat(estimatedAnnualSpendGas) / 12
  ).toFixed(2);

  const estimatedMonthlySpend = (
    (estimatedAnnualSpendElec + estimatedAnnualSpendGas) /
    12
  ).toFixed(2);

  const hasCompleteTariffInfo =
    (combinedTariffInfo &&
      REQUIRED_TARIFF_FIELDS[combinedTariffInfo?.fuelType]?.every(
        (field) =>
          combinedTariffInfo?.[field] !== null &&
          combinedTariffInfo?.[field] !== undefined
      )) ??
    false;

  const updateSupplierOptions = async (
    fuelType,
    meterTypeElec = null,
    meterTypeGas = null,
    paymentMethodElec = null,
    paymentMethodGas = null
  ) => {
    setIsSupplierDataLoading(true);
    setIsTariffDataLoading(true);
    if (normaliseFuelType(fuelType) === SUPPLIER_FUEL_TYPE.ELECGAS) {
      await fetchSupplierOptions(
        FUEL_TYPE.ELECGAS,
        normaliseMeterType(meterTypeElec),
        paymentMethodElec
      );
      await fetchSupplierOptions(
        FUEL_TYPE.ELEC,
        normaliseMeterType(meterTypeElec),
        paymentMethodElec
      );
      await fetchSupplierOptions(FUEL_TYPE.GAS, normaliseMeterType(meterTypeGas), paymentMethodGas);
    } else if (normaliseFuelType(fuelType) === SUPPLIER_FUEL_TYPE.ELEC) {
      await fetchSupplierOptions(
        FUEL_TYPE.ELEC,
        normaliseMeterType(meterTypeElec),
        paymentMethodElec
      );
    } else {
      await fetchSupplierOptions(FUEL_TYPE.GAS, normaliseMeterType(meterTypeGas), paymentMethodGas);
    }
    setIsSupplierDataLoading(false);
  };

  return (
    <TariffContext.Provider
      value={{
        isFetchingCurrentTariff,
        setIsFetchingCurrentTariff,
        isFetchingMonthlySpend,
        setIsFetchingMonthlySpend,
        meterDetails,
        currentTariff,
        quoteId,
        existingTariffElec,
        existingTariffGas,
        fetchDetails,
        getQuotes,
        getCurrentTariff,
        estimatedMonthlyKwhsElec,
        estimatedMonthlyKwhsGas,
        estimatedMonthlySpend,
        userDefinedTariffInfo,
        setUserDefinedTariffInfo,
        updateUserDefinedTariffInfo,
        deleteUserDefinedTariffInfoKey,
        fetchSwitchStatus,
        getEstimatedMonthlySpend,
        estimatedMonthlySpendElec,
        estimatedMonthlySpendGas,
        combinedTariffInfo,
        hasCompleteTariffInfo,
        resetTariffInfo,
        isTariffDataLoading,
        setIsTariffDataLoading,
        isSupplierDataLoading,
        setIsSupplierDataLoading,
        supplierOptions,
        setSupplierOptions,
        fetchSupplierOptions,
        tariffOptions,
        setTariffOptions,
        selectedSuppliers,
        setSelectedSuppliers,
        updateSupplierOptions,
        selectedFuelType,
        setSelectedFuelType,
        selectedMeterTypes,
        setSelectedMeterTypes,
        selectedPaymentMethods,
        setSelectedPaymentMethods,
        getTariffs,
        updateTariffOptions,
      }}
    >
      {children}
    </TariffContext.Provider>
  );
};

const useTariff = () => {
  const context = useContext(TariffContext);
  if (!context) {
    throw new Error("useTariff must be used within a TariffProvider");
  }
  return context;
};

export { TariffContext, TariffProvider, useTariff };
